import React, { useState, useEffect, useRef } from "react";
import { Card, Typography, Button, IconButton, Chip } from "@mhome/ui";
import { CircularProgress, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useWebSocket } from "../ws/websocket";
import { listSchedules } from "../service/schedule-service";
import { useHistory } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { spacingToPx } from "../lib/utils";

const SchedulesListView = () => {
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const { sendAndWaitWs } = useWebSocket();
  const history = useHistory();
  const muiTheme = useTheme();
  const getComponentBgColor = useComponentBackgroundColor();
  const filterDate = useRef("");
  const wsReady = useRecoilValue(wsReadyState);

  // Calculate card background color - use the same computed value for both Card and IconButton
  const cardBackgroundColor = getComponentBgColor(
    muiTheme.palette.background.paper
  );

  useEffect(() => {
    if (wsReady) {
      fetchSchedules();
    }
  }, [wsReady]);

  useEffect(() => {
    filterSchedules();
  }, [schedules, filterDate.current]);

  const fetchSchedules = async () => {
    setLoading(true);
    const response = await listSchedules(sendAndWaitWs);
    if (response && !response.error) {
      setSchedules(response.schedules);
    }
    setLoading(false);
  };

  const filterSchedules = () => {
    let filtered = schedules;

    // Filter by date
    if (filterDate.current) {
      const selectedDate = dayjs(filterDate.current);
      filtered = filtered.filter((schedule) => {
        const scheduleDate = dayjs(schedule.timestamp * 1000);
        return scheduleDate.isSame(selectedDate, "day");
      });
    }

    setFilteredSchedules(filtered);
  };

  const handleFilterDateChange = (newValue) => {
    filterDate.current = newValue ? newValue.format("YYYY-MM-DD") : "";
    filterSchedules();
  };

  const formatDateTime = (timestamp) => {
    const now = dayjs();
    const targetTime = dayjs(timestamp * 1000);
    const diffInMinutes = targetTime.diff(now, "minute");
    const diffInHours = targetTime.diff(now, "hour");

    const absoluteTime = targetTime.format("YYYY-MM-DD HH:mm:ss");

    if (diffInMinutes < 0) {
      // Past time
      return absoluteTime;
    } else if (diffInMinutes < 60) {
      // Less than 60 minutes
      return `${absoluteTime} (in ${diffInMinutes} mins)`;
    } else if (diffInHours < 24) {
      // Less than 24 hours
      return `${absoluteTime} (in ${diffInHours} hours)`;
    } else {
      // More than 24 hours
      return absoluteTime;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "SCHEDULE_AT":
        return "Schedule";
      case "SCHEDULE_RANGE":
        return "Time Range";
      default:
        return type;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "SCHEDULE_AT":
        return "primary";
      case "SCHEDULE_RANGE":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <div
      style={{
        padding: spacingToPx(2),
        backgroundColor: "transparent",
        maxWidth: 1200,
        margin: "0 auto",
      }}
    >
      <Card
        style={{
          marginBottom: spacingToPx(1),
          backgroundColor: cardBackgroundColor,
        }}
        elevation={0}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            padding: spacingToPx(1),
            gap: spacingToPx(1.5),
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: spacingToPx(1.5),
              width: "100%",
              alignItems: "center",
            }}
          >
            <div
              style={{
                flex: 1,
                height: "40px",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={filterDate.current ? dayjs(filterDate.current) : null}
                  onChange={handleFilterDateChange}
                  clearable
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      InputLabelProps: {
                        shrink: true,
                      },
                      InputProps: {
                        style: {
                          width: "100%",
                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor: cardBackgroundColor,
                          borderRadius: "16px",
                        },
                      },
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: `${cardBackgroundColor} !important`,
                          "&:hover": {
                            backgroundColor: `${cardBackgroundColor} !important`,
                          },
                          "&.Mui-focused": {
                            backgroundColor: `${cardBackgroundColor} !important`,
                          },
                        },
                        "& .MuiInputBase-root": {
                          backgroundColor: `${cardBackgroundColor} !important`,
                        },
                      },
                      style: {
                        width: "100%",
                        height: "100%",
                      },
                    },
                    actionBar: {
                      actions: ["clear"],
                    },
                  }}
                  style={{ width: "100%" }}
                />
              </LocalizationProvider>
            </div>

            <IconButton
              onClick={fetchSchedules}
              disabled={loading}
              className="hover:bg-transparent"
              style={{
                height: "40px",
                width: "40px",
                backgroundColor: cardBackgroundColor,
                borderRadius: "16px",
                padding: "6px",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor =
                    muiTheme.palette.action.hover;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = cardBackgroundColor;
                }
              }}
            >
              <RefreshIcon
                style={{
                  fontSize: 20,
                  color: loading
                    ? muiTheme.palette.text.disabled
                    : muiTheme.palette.text.primary,
                  animation: loading ? "spin 1s linear infinite" : "none",
                }}
              />
            </IconButton>
          </div>
        </div>
      </Card>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: spacingToPx(4),
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <>
          {filteredSchedules.length > 0 ? (
            <div style={{ display: "grid", gap: spacingToPx(1) }}>
              {filteredSchedules.map((schedule, index) => (
                <Card
                  key={index}
                  style={{
                    padding: spacingToPx(2),
                    backgroundColor: getComponentBgColor("hsl(var(--card))"),
                    boxShadow: "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ flex: 1, marginRight: spacingToPx(1.5) }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: spacingToPx(1),
                          flexWrap: "wrap",
                          gap: spacingToPx(0.5),
                        }}
                      >
                        {schedule.automationId && (
                          <Chip
                            label="Automation"
                            size="small"
                            color="primary"
                            variant="outlined"
                            style={{
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              height: 20,
                            }}
                          />
                        )}
                        <Chip
                          label={getTypeLabel(schedule.type)}
                          size="small"
                          color={getTypeColor(schedule.type)}
                          style={{
                            fontWeight: 500,
                            fontSize: "0.7rem",
                            height: 20,
                          }}
                        />
                      </div>

                      <Typography
                        variant="h6"
                        style={{
                          marginBottom: spacingToPx(0.5),
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.2,
                          fontSize: "1rem",
                          fontWeight: 600,
                        }}
                        className="text-foreground"
                      >
                        {schedule.automationName}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        {formatDateTime(schedule.timestamp)}
                      </Typography>
                    </div>

                    {schedule.automationId && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          history.push(
                            `/automation/overview?id=${schedule.automationId}`
                          );
                        }}
                        style={{
                          backgroundColor: "hsl(var(--background))",
                          color: "white",
                          width: 32,
                          height: 32,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "hsl(var(--accent))";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "hsl(var(--background))";
                        }}
                      >
                        <ArrowForwardIcon
                          fontSize="small"
                          style={{
                            color: muiTheme.palette.text.secondary,
                          }}
                        />
                      </IconButton>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                marginTop: spacingToPx(6),
                paddingTop: spacingToPx(4),
                paddingBottom: spacingToPx(4),
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                borderRadius: spacingToPx(4),
              }}
            >
              <Typography
                variant="h6"
                color="textSecondary"
                style={{ marginBottom: spacingToPx(1) }}
              >
                No schedules available
              </Typography>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SchedulesListView;
